import { __decorate } from "tslib";
import { html } from 'lit';
import { CipublicElement } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/base.js';
import { property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { InternalI18n } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/i18n/internal-i18n-controller.js';
import { emit, modalToggleEvent } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/event.js';
import { configContext } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/config.js';
import { contextProvided } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/context/decorators/context-provided.js';
import { ContextConsumer } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/context/controllers/context-consumer.js';
import { dynamicLinksContext } from '@cigna/cigna_csg_gsg_campaigns/lib/interfaces.js';
import { addStyleSheetToElements } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/cssstylesheet-interface.js';
import { updateDynamicLinks } from './service.js';
import { isExternalLinkContext, defaultIsExternalLink } from './interfaces.js';
import '@cigna/cigna_csg_gsg_cipublic-icon/cipublic-icon.js';
import styles from './link.scss.js';
import animations from './animations.scss.js';
import iconLinkStyles from './icon-link.scss.js';
/**
 * cipublic-link
 *
 * @slot prefix - `cipublic-icon` **before** the link text
 * @slot suffix - `cipublic-icon` **after** the link text
 * @slot
 */
export class Link extends CipublicElement {
    static get styles() {
        return [styles, animations];
    }
    set rel(val) {
        const oldVal = this._rel;
        this._rel = val;
        this.requestUpdate('rel', oldVal);
    }
    /**
     * The relationship of the linked URL as space-separated link types. `noopener` if target is `_blank` and `noreferrer`/`noopener` have not been set.
     */
    // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
    get rel() {
        var _a;
        const parts = ((_a = this._rel) === null || _a === void 0 ? void 0 : _a.split(' ')) || [];
        if (this.target === '_blank') {
            if (!parts.some((part) => ['noreferrer', 'noopener'].includes(part))) {
                parts.push('noopener');
            }
        }
        return parts.join(' ');
    }
    emitEvent(event) {
        if (this.isModalToggle) {
            modalToggleEvent(this, event);
        }
        else {
            emit(this, `${this._name}.click`, {
                detail: { modalToggle: this.isModalToggle, modalTarget: this.modalTarget, event },
            });
        }
    }
    constructor() {
        super();
        this.i18n = new InternalI18n(this);
        this.noBold = false;
        this.iconHasBeenAdded = false;
        /**
         * Link variant.
         */
        this.variant = 'primary';
        // @property({ type: Boolean })
        this.isExternalLink = false;
        /**
         *  Checker to make it a Modal button
         */
        this.isModalToggle = false;
        /**
         * Constructor name.
         */
        this._name = 'link';
        // function to track if link is external
        this.checkExternalLink = defaultIsExternalLink;
        new ContextConsumer(this, dynamicLinksContext, (value) => (this.campaignsObj = value), true);
        // if we're passing in more than the dental link, we'll want to revist how we're passing
        // into the link campaign service
        new ContextConsumer(this, configContext, (config) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.endpoint = config.endpoints.dentalCampaign;
        });
    }
    willUpdate(changedProperties) {
        super.willUpdate(changedProperties);
        if (changedProperties.has('href') || changedProperties.has('checkExternalLink')) {
            let url;
            try {
                url = new URL(this.href || '', location.origin);
            }
            catch (error) {
                url = new URL('', location.origin);
            }
            this.isExternalLink = this.checkExternalLink(url);
            if (this.isExternalLink) {
                this.target = '_blank';
            }
        }
        if (changedProperties.has('campaignsObj')) {
            if (this.campaignsObj && this.href) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const updatedHref = updateDynamicLinks(this.href || '', this.campaignsObj, this.endpoint);
                this.href = updatedHref.length ? updatedHref : this.href;
            }
        }
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('link.updated', this.linkUpdated);
    }
    disconnectedCallback() {
        this.removeEventListener('link.updated', this.linkUpdated);
    }
    async firstUpdated(changedProperties) {
        super.firstUpdated(changedProperties);
        await this.updateComplete;
        this.beginLinkCheck();
    }
    linkUpdated() {
        if (this.querySelector('span[slot="main-content"]')) {
            this.querySelectorAll('span[slot="main-content"]').forEach((element) => {
                var _a;
                (_a = element.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(element);
            });
            this.iconHasBeenAdded = false;
        }
        this.beginLinkCheck();
    }
    decodeEntities(markup) {
        return markup.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    }
    checkForText() {
        return this.innerHTML.length > 0;
    }
    beginLinkCheck() {
        if (this.isImage)
            return;
        else if (this.checkForText())
            this.renderLinkText();
        else
            setTimeout(() => this.beginLinkCheck(), 500);
    }
    renderLinkText() {
        if (this.iconHasBeenAdded) {
            if (!this.querySelector("[slot='prefix']"))
                this.addSuffixStyles();
            return;
        }
        if (this.querySelector("[slot='prefix']"))
            this.renderPrefix(this.textContent);
        else
            this.renderSuffix(this.decodeEntities(this.innerHTML));
        this.iconHasBeenAdded = true;
    }
    renderIcon(set, name, size = 'small') {
        const isActionLink = name === 'chevron-action';
        const isExternalLink = name === 'external-link';
        const isSecure = name === 'lock';
        const isExternalOrSecure = isExternalLink ? 'link_external' : 'secure';
        return `<span style="align-items: baseline;"><cipublic-icon set="${set}" name="${name}" role="img" size="${size}" class="${isActionLink ? 'icon-as-placeholder' : ''}" aria-label="${isExternalLink || isSecure ? this.i18n.t(isExternalOrSecure) : name}"></cipublic-icon></span>`;
    }
    renderActionIcon() {
        return html `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 32 32" aria-hidden="true"><g fill="none" stroke-linejoin="round" stroke-miterlimit="10"><circle cx="16" cy="16" r="15.12"></circle><path fill="currentColor" d="M12.227 8.852a.563.563 0 0 1 .797 0l6.75 6.75a.563.563 0 0 1 0 .797l-6.75 6.75a.563.563 0 0 1-.797-.796L18.58 16 12.227 9.648a.563.563 0 0 1 0-.796z"></path></g></svg>`;
    }
    renderPrefix(linkText) {
        this.insertAdjacentHTML('beforeend', `<span slot="main-content">${linkText}</span>`);
    }
    renderLinkStyles() {
        return `<style>[link-slot] sup {font-size: 0.75em; line-height: normal !important; top: 0 !important;}</style>`;
    }
    renderSuffix(linkText) {
        const words = linkText.trim().split(/(\w+(?=\/)|\w+(?=[-])|\s|\/\/|<[>]*>|@|%)/);
        const joinWords = (words) => words.map((item) => (item === ' ' ? item.replace(' ', '&nbsp;') : item)).join('');
        const lastWord = words[words.length - 1].length <= 1 ? joinWords(words.splice(-2)) : words.splice(-1).join('');
        const firstPart = words.join('');
        const getIcon = () => {
            if (this.type === 'action')
                return this.renderIcon('system', 'chevron-action');
            if (this.isSecure && this.isExternalLink)
                return `${this.renderIcon('system', 'lock')}`;
            if (this.isSecure)
                return this.renderIcon('system', 'lock');
            if (this.isExternalLink)
                return this.renderIcon('system', 'external-link');
            else
                return `<slot name="suffix"></slot>`;
        };
        this.insertAdjacentHTML('beforeend', `<span slot="main-content" link-slot>${firstPart}<span style="display: inline-flex; line-height: normal; text-decoration: inherit; text-decoration-color: inherit; white-space: nowrap;">${this.renderLinkStyles()}${lastWord}${getIcon()}</span></span>`);
        this.addSuffixStyles();
    }
    addSuffixStyles() {
        const iconLink = this.querySelectorAll('cipublic-icon');
        addStyleSheetToElements(iconLink, iconLinkStyles);
    }
    renderATag() {
        return html `<a href="${ifDefined(this.href)}" lang="${ifDefined(this.hreflang)}" target="${ifDefined(this.target)}" download="${ifDefined(this.download)}" rel="${ifDefined(this.rel)}" aria-label="${ifDefined(this.ariaLabeling)}" @click="${() => emit(this, 'link.click')}"><slot name="prefix"></slot><slot name="main-content" class="${this.noBold === true ? 'no-bold' : ''}"></slot>${this.type === 'action'
            ? this.renderActionIcon()
            : ''}</a>`;
    }
    // !Note: be careful here and with `renderATag()`, be sure not to add *any* extra space in html``
    render() {
        if (this.isButton) {
            return html `<button @click="${(event) => {
                this.emitEvent(event);
            }}" type="button" aria-label="${ifDefined(this.ariaLabeling)}"><slot name="prefix"></slot><slot name="main-content" class="${this.noBold === true ? 'no-bold' : ''}"></slot>${this.type ===
                'action'
                ? this.renderActionIcon()
                : ''}</button>`;
        }
        return this.renderATag();
    }
}
__decorate([
    state()
], Link.prototype, "endpoint", void 0);
__decorate([
    property({ reflect: true })
], Link.prototype, "href", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Link.prototype, "nounderline", void 0);
__decorate([
    property({ reflect: true })
], Link.prototype, "hreflang", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Link.prototype, "isButton", void 0);
__decorate([
    property({ reflect: true, type: Boolean, attribute: 'is-image-link' })
], Link.prototype, "isImage", void 0);
__decorate([
    property({ reflect: true })
], Link.prototype, "target", void 0);
__decorate([
    property({ reflect: true })
], Link.prototype, "download", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Link.prototype, "noBold", void 0);
__decorate([
    property({ reflect: true })
], Link.prototype, "rel", null);
__decorate([
    property({ reflect: true, type: Boolean })
], Link.prototype, "iconHasBeenAdded", void 0);
__decorate([
    property({ reflect: true })
], Link.prototype, "type", void 0);
__decorate([
    property({ reflect: true })
], Link.prototype, "variant", void 0);
__decorate([
    state()
], Link.prototype, "isExternalLink", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Link.prototype, "isSecure", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], Link.prototype, "isModalToggle", void 0);
__decorate([
    property({ attribute: 'data-modal-target' })
], Link.prototype, "modalTarget", void 0);
__decorate([
    property({ reflect: true, attribute: 'arialabeling' })
], Link.prototype, "ariaLabeling", void 0);
__decorate([
    contextProvided({ context: isExternalLinkContext }),
    state()
], Link.prototype, "checkExternalLink", void 0);
__decorate([
    state()
], Link.prototype, "campaignsObj", void 0);
//# sourceMappingURL=link.js.map